/* ============================================================
   Pawomics — Free Dog & Cat Gut Health Quiz
   assets/pawomics-gut-quiz.js

   Vanilla JS, no external libraries, no API keys.
   Flow: intro → 10 questions → Shopify email consent gate → full result.

   Quiz version: gut-v3.2
   Focus: stool pattern | digestive comfort | food-linked pattern |
          recent gut disruption
   ============================================================ */

(function () {
  'use strict';

  /* ============================================================
     1. QUIZ CONFIG

     Score keys:
     - stoolPattern
     - digestiveComfort
     - foodLinked
     - gutDisruption

     Age is used for context only. It does not increase symptom
     severity or signal strength.
     ============================================================ */
  var QUIZ_VERSION = 'gut-v3.2';
  var STORAGE_PENDING_KEY = 'pawomicsGutQuizPendingV32';
  var STORAGE_COMPLETE_KEY = 'pawomicsGutQuizCompleteV32';

  var QUESTIONS = [
    {
      id: 'pet_type',
      text: 'Is your pet a dog or a cat?',
      options: [
        { label: 'Dog', value: 'dog' },
        { label: 'Cat', value: 'cat' }
      ]
    },
    {
      id: 'age_group',
      text: 'How old is your pet?',
      options: [
        { label: 'Under 1 year', value: 'under_1' },
        { label: '1–3 years', value: 'age_1_3' },
        { label: '4–7 years', value: 'age_4_7' },
        { label: '8–10 years', value: 'age_8_10' },
        { label: '11+ years', value: 'age_11_plus' }
      ]
    },
    {
      id: 'main_concern',
      text: 'What is your main concern about your pet’s gut health?',
      options: [
        {
          label: 'Loose or inconsistent stools',
          value: 'loose_inconsistent_stools',
          score: { stoolPattern: 2 }
        },
        {
          label: 'Constipation or difficulty passing stool',
          value: 'constipation',
          score: { stoolPattern: 2 }
        },
        {
          label: 'Frequent digestive upset',
          value: 'frequent_digestive_upset',
          score: { digestiveComfort: 2 }
        },
        {
          label: 'Sudden or unexplained weight changes',
          value: 'sudden_weight_changes',
          redFlag: 'sudden_or_unexplained_weight_change'
        },
        {
          label: 'General gut wellness',
          value: 'general_gut_wellness'
        }
      ]
    },
    {
      id: 'stool_quality',
      text: 'What is your pet’s stool like most days?',
      options: [
        { label: 'Firm and consistent', value: 'firm', score: { stoolPattern: 0 } },
        { label: 'Slightly soft', value: 'slightly_soft', score: { stoolPattern: 1 } },
        { label: 'Loose', value: 'loose', score: { stoolPattern: 3 } },
        {
          label: 'Very loose or watery',
          value: 'very_loose_watery',
          score: { stoolPattern: 4 },
          redFlag: 'very_loose_or_watery_stool_most_days'
        },
        { label: 'It varies from day to day', value: 'varies', score: { stoolPattern: 2 } }
      ]
    },
    {
      id: 'gas_bloating',
      text: 'How often do you notice gas, bloating, or stomach noises?',
      options: [
        { label: 'Never', value: 'never', score: { digestiveComfort: 0 } },
        { label: 'Rarely', value: 'rarely', score: { digestiveComfort: 0 } },
        { label: 'Sometimes', value: 'sometimes', score: { digestiveComfort: 1 } },
        { label: 'Often', value: 'often', score: { digestiveComfort: 3 } }
      ]
    },
    {
      id: 'vomiting_hairball',
      text: 'How often does your pet vomit?',
      options: [
        {
          label: 'Never or less than once a month',
          value: 'less_than_monthly',
          score: { digestiveComfort: 0 }
        },
        {
          label: 'About once a month',
          value: 'monthly',
          score: { digestiveComfort: 1 }
        },
        {
          label: 'Once a week or more',
          value: 'weekly_or_more',
          score: { digestiveComfort: 3 },
          redFlag: 'vomiting_or_hairballs_weekly_or_more'
        }
      ]
    },
    {
      id: 'food_trigger',
      text: 'Which foods seem to cause digestive upset for your pet?',
      options: [
        { label: 'Chicken', value: 'chicken', score: { foodLinked: 3 } },
        { label: 'Beef', value: 'beef', score: { foodLinked: 3 } },
        { label: 'Grains', value: 'grains', score: { foodLinked: 3 } },
        { label: 'Rich or fatty foods', value: 'rich_fatty', score: { foodLinked: 3 } },
        { label: 'Multiple foods', value: 'multiple_foods', score: { foodLinked: 4 } },
        { label: 'None that I have noticed', value: 'none_noticed', score: { foodLinked: 0 } },
        { label: 'Not sure', value: 'not_sure', score: { foodLinked: 1 } }
      ]
    },
    {
      id: 'warning_signs',
      text: 'Has your pet shown any of these warning signs recently?',
      options: [
        { label: 'Blood or black stool', value: 'blood_black_stool', redFlag: 'blood_or_black_stool' },
        { label: 'Repeated vomiting', value: 'repeated_vomiting', redFlag: 'repeated_vomiting' },
        { label: 'Loss of appetite', value: 'loss_of_appetite', redFlag: 'loss_of_appetite' },
        {
          label: 'Sudden or unexplained weight loss',
          value: 'sudden_weight_loss',
          redFlag: 'sudden_or_unexplained_weight_loss'
        },
        {
          label: 'Severe lethargy or signs of pain',
          value: 'lethargy_or_pain',
          redFlag: 'severe_lethargy_or_pain'
        },
        {
          label: 'More than one of these',
          value: 'multiple_warning_signs',
          redFlag: 'multiple_warning_signs'
        },
        { label: 'None of these', value: 'none' }
      ]
    },
    {
      id: 'diet_pattern',
      text: 'Which best describes your pet’s usual diet?',
      options: [
        {
          label: 'Mostly the same food every day',
          value: 'same_food_daily',
          score: { foodLinked: 0, gutDisruption: 0 }
        },
        {
          label: 'A mix of different commercial foods',
          value: 'mixed_commercial',
          score: { foodLinked: 1, gutDisruption: 1 }
        },
        {
          label: 'Raw or home-cooked food',
          value: 'raw_home_cooked',
          score: { foodLinked: 1, gutDisruption: 1 }
        },
        {
          label: 'Foods change frequently',
          value: 'frequent_food_changes',
          score: { foodLinked: 3, gutDisruption: 1 }
        },
        {
          label: 'Very picky or inconsistent eating',
          value: 'picky_inconsistent',
          score: { foodLinked: 2, gutDisruption: 1 }
        }
      ]
    },
    {
      id: 'antibiotics_probiotics',
      text: 'Has your pet recently taken antibiotics, probiotics, or digestive supplements?',
      options: [
        { label: 'None', value: 'none', score: { gutDisruption: 0 } },
        {
          label: 'Antibiotics within the last 3 months',
          value: 'antibiotics_3mo',
          score: { gutDisruption: 4 }
        },
        {
          label: 'Probiotics occasionally',
          value: 'probiotics_occasional',
          score: { gutDisruption: 1 }
        },
        {
          label: 'Probiotics daily',
          value: 'probiotics_daily',
          score: { gutDisruption: 1 }
        },
        {
          label: 'Other digestive supplements',
          value: 'other_digestive_supplements',
          score: { gutDisruption: 1 }
        },
        { label: 'Not sure', value: 'not_sure', score: { gutDisruption: 1 } }
      ]
    }
  ];

  /* ---------- Result content shown on-site and sent in payload ---------- */
  var RESULTS = {
    balanced: {
      type: 'Balanced Gut Pattern',
      headline: 'Your pet’s gut signals look relatively steady right now.',
      explanation: 'Based on your answers, no single digestive pattern stood out strongly. This does not confirm that everything is perfect, but it suggests that baseline tracking and preventive care may be the most useful next steps.',
      insights: [
        'Your pet’s stool and digestive-comfort answers appear relatively stable.',
        'A simple baseline makes future changes easier to notice.',
        'Diet changes, stress, medications, and age can shift gut patterns over time.'
      ],
      tracking: [
        'Track stool consistency and frequency a few times this week.',
        'Note any new foods, treats, or supplements.',
        'Watch for changes in appetite, energy, gas, or vomiting.'
      ],
    },
    stool: {
      type: 'Stool Consistency Pattern',
      headline: 'Your pet’s stool pattern is the clearest signal in your answers.',
      explanation: 'You reported stool changes such as softness, looseness, constipation, or day-to-day variation. These changes can have many causes, so the most useful first step is to track consistency, frequency, diet, and timing together.',
      insights: [
        'Stool consistency is one of the clearest day-to-day gut signals.',
        'Food changes, treats, stress, and medications can all affect stool patterns.',
        'Persistent diarrhea, constipation, straining, or worsening symptoms should be discussed with a veterinarian.'
      ],
      tracking: [
        'Record stool consistency and frequency each day.',
        'Note new foods, treats, table scraps, or diet changes.',
        'Track straining, urgency, appetite, and energy at the same time.'
      ],
    },
    sensitive: {
      type: 'Sensitive Digestion Pattern',
      headline: 'Your pet may have a sensitive digestion pattern.',
      explanation: 'Your answers point to digestive discomfort such as gas, bloating, stomach noises, or vomiting. This quiz cannot identify a cause, but tracking when these signs occur may help you and your veterinarian see a clearer pattern.',
      insights: [
        'Timing matters: note whether symptoms occur before or after meals.',
        'Meal size, eating speed, treats, and food changes may influence digestive comfort.',
        'Frequent or worsening vomiting, pain, appetite loss, or lethargy needs veterinary attention.'
      ],
      tracking: [
        'Record gas, bloating, stomach noises, or vomiting each day.',
        'Note meal times, portion size, treats, and eating speed.',
        'Track stool consistency and appetite alongside digestive discomfort.'
      ],
    },
    foodLinked: {
      type: 'Possible Food-Linked Digestive Pattern',
      headline: 'Your answers suggest that certain foods may be linked to digestive upset.',
      explanation: 'You reported a possible relationship between food and digestive changes. This does not confirm a food allergy or intolerance. A careful food-and-symptom record is more useful than removing several ingredients at once.',
      insights: [
        'A reported food trigger is a pattern to investigate, not a confirmed allergy.',
        'Changing one variable at a time makes food-related patterns easier to interpret.',
        'Major elimination diets should be planned with a veterinarian or qualified nutrition professional.'
      ],
      tracking: [
        'Record the exact food, treat, or ingredient eaten before symptoms appear.',
        'Note stool, gas, vomiting, appetite, and symptom timing.',
        'Avoid changing several foods or supplements at the same time.'
      ],
    },
    postAntibiotic: {
      type: 'Post-Antibiotic Gut Watch Pattern',
      headline: 'Recent antibiotics are an important part of your pet’s gut-health picture.',
      explanation: 'Antibiotics can temporarily change gut bacteria, although the effect varies by pet. Your answers do not prove that antibiotics caused current symptoms, but the timing is worth tracking and discussing with your veterinarian.',
      insights: [
        'The antibiotic name, dates, and reason for use provide useful context.',
        'Track stool, appetite, gas, and vomiting before adding several new products.',
        'Ask your veterinarian whether and when a probiotic is appropriate for your pet.'
      ],
      tracking: [
        'Write down the antibiotic name and the dates it was given.',
        'Track stool consistency, appetite, gas, and vomiting daily.',
        'Record any probiotic or digestive supplement separately.'
      ],
    },
    vetFirst: {
      type: 'Veterinary Check Recommended',
      headline: 'One of your answers should be checked by a veterinarian first.',
      explanation: 'You reported a warning sign that an online quiz cannot safely evaluate. Contact your veterinarian, especially if the sign is persistent, worsening, painful, or accompanied by reduced appetite, weakness, repeated vomiting, or blood in the stool.',
      insights: [
        'Warning signs can have many causes and should not be interpreted through a quiz alone.',
        'The timing, frequency, and severity of the sign will help your veterinarian assess it.',
        'Gut testing may provide additional context later, but it should not delay veterinary care.'
      ],
      tracking: [
        'Contact your veterinarian and describe the warning sign clearly.',
        'Record when it started, how often it occurs, and whether it is worsening.',
        'Bring a list of recent foods, medications, antibiotics, and supplements.'
      ],
    }
  };

  /* ---------- Signal breakdown ----------
     0–1 = Low | 2–3 = Mild | 4–5 = Moderate | 6+ = Strong
  ---------------------------------------------------------------- */
  var BREAKDOWN_ROWS = [
    { label: 'Stool Pattern', key: 'stoolPattern' },
    { label: 'Digestive Comfort', key: 'digestiveComfort' },
    { label: 'Food-Linked Pattern', key: 'foodLinked' },
    { label: 'Recent Gut Disruption', key: 'gutDisruption' }
  ];

  var PRIORITY_BY_RESULT = {
    balanced: 'Keep a simple baseline. Track stool, appetite, gas, and major food changes so future shifts are easier to notice.',
    stool: 'Track stool consistency and frequency beside food changes. Avoid changing several parts of the diet at once.',
    sensitive: 'Track digestive discomfort beside meals. Note timing, portions, treats, eating speed, stool, and appetite.',
    foodLinked: 'Keep a food-and-symptom record. Treat the reported food as a possible pattern, not a confirmed allergy.',
    postAntibiotic: 'Record the antibiotic timeline and current digestive signs. Discuss probiotic timing with your veterinarian.',
    vetFirst: 'Veterinary guidance comes first. Do not delay care while trying new foods, supplements, or at-home tests.'
  };


  /* ---------- Result-specific next-step recommendations ----------
     Product URLs are read from data attributes on #pgq-root so the
     destination can be changed in Liquid without editing this file.
  ---------------------------------------------------------------- */
  var RECOMMENDATIONS = {
    balanced: {
      title: 'Build a baseline before something changes.',
      body: 'Your pet’s signals look relatively steady. A gut microbiome test can establish a baseline now, making future changes easier to compare.',
      primary: {
        label: 'Get a Baseline Gut Report',
        product: 'test',
        trackingContent: 'balanced_test'
      },
      note: 'Best for pet parents who want a proactive reference point rather than waiting for symptoms to become obvious.'
    },
    stool: {
      title: 'Look beyond stool consistency.',
      body: 'Stool changes can have several possible causes. A gut microbiome test provides a more detailed view of bacterial patterns and personalized food guidance than this quiz can offer.',
      primary: {
        label: 'See What’s Happening in the Gut',
        product: 'test',
        trackingContent: 'stool_test'
      },
      secondary: {
        label: 'View the Test + Probiotics Bundle',
        product: 'bundle',
        trackingContent: 'stool_bundle'
      }
    },
    sensitive: {
      title: 'Separate digestive patterns from guesswork.',
      body: 'Testing can provide a clearer gut profile, while a daily probiotic may help support digestive health and maintain healthy gut flora. Choose the next step that best matches what you want to understand or support.',
      primary: {
        label: 'Explore the Gut Microbiome Test',
        product: 'test',
        trackingContent: 'sensitive_test'
      },
      secondary: {
        label: 'Explore Daily Probiotic Support',
        product: 'probiotics',
        trackingContent: 'sensitive_probiotics'
      }
    },
    foodLinked: {
      title: 'Investigate before removing multiple foods.',
      body: 'This quiz cannot confirm a food allergy or intolerance. A gut microbiome test can provide personalized food and fiber guidance before you make several diet changes at once.',
      primary: {
        label: 'Get Personalized Gut Insights',
        product: 'test',
        trackingContent: 'food_linked_test'
      },
      tertiary: {
        label: 'See what a full sample report includes',
        product: 'sample',
        trackingContent: 'food_linked_sample_report'
      }
    },
    postAntibiotic: {
      title: 'Pair clearer insight with daily gut support.',
      body: 'Recent antibiotics are useful context when reviewing gut health. Testing can show a more detailed gut profile, while probiotic support may be worth discussing with your veterinarian based on your pet’s current needs.',
      primary: {
        label: 'Explore the Test + Probiotics Bundle',
        product: 'bundle',
        trackingContent: 'post_antibiotic_bundle'
      },
      secondary: {
        label: 'View Daily Probiotic Support',
        product: 'probiotics',
        trackingContent: 'post_antibiotic_probiotics'
      },
      note: 'Ask your veterinarian about probiotic timing if your pet is still taking medication or has ongoing symptoms.'
    },
    vetFirst: {
      title: 'Veterinary guidance comes first.',
      body: 'Contact your veterinarian before making major diet or supplement changes. Gut testing may provide additional context later, but it should not delay appropriate veterinary care.',
      primary: {
        label: 'Learn About Gut Testing for Later',
        product: 'test',
        trackingContent: 'vet_first_test_later',
        style: 'secondary'
      },
      note: 'This recommendation is intentionally low-pressure because the warning sign you reported needs professional review first.',
      tone: 'vetFirst'
    }
  };

  var AGE_INSIGHTS = {
    under_1: 'Young pets can be sensitive to sudden diet changes, so introduce new foods gradually unless your veterinarian advises otherwise.',
    age_1_3: 'Early-adult baseline tracking can make future digestive changes easier to recognize.',
    age_4_7: 'Midlife diet, activity, medications, and stress can all influence digestive patterns.',
    age_8_10: 'For older pets, new digestive or weight changes deserve closer monitoring and veterinary context.',
    age_11_plus: 'In senior pets, new stool, appetite, vomiting, or weight changes should be discussed with a veterinarian promptly.'
  };

  function getLevel(score) {
    if (score >= 6) return 'Strong';
    if (score >= 4) return 'Moderate';
    if (score >= 2) return 'Mild';
    return 'Low';
  }

  function getLevelStep(level) {
    return { Low: 1, Mild: 2, Moderate: 3, Strong: 4 }[level] || 1;
  }

  /* ============================================================
     2. STATE
     ============================================================ */
  var state = {
    currentIndex: 0,
    answers: {},
    scores: {
      stoolPattern: 0,
      digestiveComfort: 0,
      foodLinked: 0,
      gutDisruption: 0
    },
    redFlags: [],
    resultKey: null,
    signalStrength: null,
    emailSaved: false
  };

  /* ============================================================
     3. DOM REFERENCES
     ============================================================ */
  var root = document.getElementById('pgq-root');
  if (!root) return;

  var PRODUCT_URLS = {
    test: root.getAttribute('data-test-url') || 'https://pawomics.com/products/gut-microbiome-test',
    probiotics: root.getAttribute('data-probiotics-url') || 'https://pawomics.com/products/veterinary-probiotics-4',
    bundle: root.getAttribute('data-bundle-url') || 'https://pawomics.com/products/gut-microbiome-test-probiotics-bundle-dogs-cats',
    sample: root.getAttribute('data-sample-report-url') || 'https://pawomics.com/pages/gut-microbiome-sample-report'
  };

  var el = {
    screens: {
      intro: document.getElementById('pgq-screen-intro'),
      quiz: document.getElementById('pgq-screen-quiz'),
      email: document.getElementById('pgq-screen-email'),
      result: document.getElementById('pgq-screen-result')
    },
    startBtn: document.getElementById('pgq-start-btn'),
    backBtn: document.getElementById('pgq-back-btn'),
    progressLabel: document.getElementById('pgq-progress-label'),
    progressBar: document.getElementById('pgq-progressbar'),
    progressFill: document.getElementById('pgq-progress-fill'),
    questionText: document.getElementById('pgq-question-text'),
    answers: document.getElementById('pgq-answers'),
    captureForm: document.getElementById('pgq-shopify-capture-form'),
    formStatus: document.getElementById('pgq-shopify-form-status'),
    shopifyTags: document.getElementById('pgq-shopify-tags'),
    submitBtn: document.getElementById('pgq-submit-btn'),
    formError: document.getElementById('pgq-form-error'),
    firstName: document.getElementById('pgq-first-name'),
    email: document.getElementById('pgq-email'),
    petName: document.getElementById('pgq-pet-name'),
    consent: document.getElementById('pgq-consent'),
    captureSuccess: document.getElementById('pgq-capture-success'),
    redflag: document.getElementById('pgq-redflag'),
    resultType: document.getElementById('pgq-result-type'),
    resultHeadline: document.getElementById('pgq-result-headline'),
    signalStrength: document.getElementById('pgq-signal-strength'),
    resultExplanation: document.getElementById('pgq-result-explanation'),
    resultStandout: document.getElementById('pgq-result-standout'),
    resultInsights: document.getElementById('pgq-result-insights'),
    resultTracking: document.getElementById('pgq-result-tracking'),
    recommendation: document.getElementById('pgq-recommendation'),
    recommendationTitle: document.getElementById('pgq-recommendation-title'),
    recommendationBody: document.getElementById('pgq-recommendation-body'),
    recommendationPrimary: document.getElementById('pgq-recommendation-primary'),
    recommendationSecondary: document.getElementById('pgq-recommendation-secondary'),
    recommendationTertiary: document.getElementById('pgq-recommendation-tertiary'),
    recommendationNote: document.getElementById('pgq-recommendation-note'),
    fullResult: document.getElementById('pgq-full-result'),
    fullResultDynamic: document.getElementById('pgq-full-result-dynamic')
  };

  /* ============================================================
     4. HELPERS
     ============================================================ */
  function showScreen(name) {
    Object.keys(el.screens).forEach(function (key) {
      el.screens[key].hidden = (key !== name);
    });
    window.scrollTo({ top: root.offsetTop - 20, behavior: 'auto' });
  }

  function getQuestionById(questionId) {
    return QUESTIONS.find(function (question) {
      return question.id === questionId;
    });
  }

  function getSelectedOption(questionId) {
    var question = getQuestionById(questionId);
    if (!question) return null;

    return question.options.find(function (option) {
      return option.value === state.answers[questionId];
    }) || null;
  }

  function getSelectedLabel(questionId) {
    var option = getSelectedOption(questionId);
    return option ? option.label : '';
  }

  function getQuestionText(question) {
    if (question.id !== 'vomiting_hairball') return question.text;

    if (state.answers.pet_type === 'cat') {
      return 'How often does your cat vomit or bring up hairballs?';
    }
    if (state.answers.pet_type === 'dog') {
      return 'How often does your dog vomit?';
    }
    return 'How often does your pet vomit or bring up hairballs?';
  }

  function dedupe(values) {
    return values.filter(function (value, index, array) {
      return array.indexOf(value) === index;
    });
  }

  function buildAnswerLabels() {
    var labels = {};
    QUESTIONS.forEach(function (question) {
      labels[question.id] = getSelectedLabel(question.id);
    });
    return labels;
  }


  function buildTrackedProductUrl(productKey, trackingContent) {
    var baseUrl = PRODUCT_URLS[productKey] || PRODUCT_URLS.test;
    var separator = baseUrl.indexOf('?') === -1 ? '?' : '&';

    return baseUrl + separator + [
      'utm_source=gut_quiz',
      'utm_medium=onsite',
      'utm_campaign=quiz_recommendation',
      'utm_content=' + encodeURIComponent(trackingContent || state.resultKey || 'result')
    ].join('&');
  }

  function updateShopifyTags() {
    if (!el.shopifyTags) return;

    var tags = [
      'newsletter',
      'gut-health-quiz',
      'gut-result-' + state.resultKey,
      'gut-signal-' + String(state.signalStrength || 'unknown').toLowerCase()
    ];

    if (state.answers.pet_type) {
      tags.push('gut-pet-' + state.answers.pet_type);
    }

    el.shopifyTags.value = tags.join(',');
  }

  function trackRecommendationClick(event) {
    var link = event.target.closest('[data-pgq-recommendation-link]');
    if (!link) return;

    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({
        event: 'gut_quiz_recommendation_click',
        gut_quiz_result: state.resultKey,
        gut_quiz_signal: state.signalStrength,
        recommendation_product: link.getAttribute('data-product') || '',
        recommendation_position: link.getAttribute('data-position') || '',
        recommendation_label: link.textContent.trim()
      });
    }
  }

  function readStoredJSON(key) {
    try {
      var raw = window.sessionStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      console.warn('[Pawomics Quiz] Could not read saved quiz state:', error);
      return null;
    }
  }

  function writeStoredJSON(key, value) {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      console.warn('[Pawomics Quiz] Could not save quiz state:', error);
      return false;
    }
  }

  function removeStoredValue(key) {
    try {
      window.sessionStorage.removeItem(key);
    } catch (error) {
      console.warn('[Pawomics Quiz] Could not clear quiz state:', error);
    }
  }

  function buildStoredSubmission() {
    return {
      quizVersion: QUIZ_VERSION,
      answers: state.answers,
      scores: state.scores,
      redFlags: state.redFlags,
      resultKey: state.resultKey,
      signalStrength: state.signalStrength,
      firstName: el.firstName.value.trim(),
      email: el.email.value.trim(),
      petName: el.petName.value.trim(),
      submittedAt: new Date().toISOString()
    };
  }

  function restoreStoredSubmission(saved) {
    if (!saved) return false;

    state.answers = saved.answers || {};
    state.scores = saved.scores || {
      stoolPattern: 0,
      digestiveComfort: 0,
      foodLinked: 0,
      gutDisruption: 0
    };
    state.redFlags = saved.redFlags || [];
    state.resultKey = saved.resultKey || null;
    state.signalStrength = saved.signalStrength || null;

    if (el.firstName) el.firstName.value = saved.firstName || '';
    if (el.email) el.email.value = saved.email || '';
    if (el.petName) el.petName.value = saved.petName || '';
    if (el.consent) el.consent.checked = true;

    return Boolean(state.resultKey);
  }

  /* ============================================================
     5. QUESTION RENDERING
     ============================================================ */
  function renderQuestion() {
    var question = QUESTIONS[state.currentIndex];
    var total = QUESTIONS.length;
    var number = state.currentIndex + 1;

    el.progressLabel.textContent = 'Question ' + number + ' of ' + total;
    el.progressBar.setAttribute('aria-valuemax', String(total));
    el.progressBar.setAttribute('aria-valuenow', String(number));
    el.progressFill.style.width = (number / total * 100) + '%';

    el.questionText.textContent = getQuestionText(question);
    el.answers.innerHTML = '';

    question.options.forEach(function (option) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'pgq__answer';
      button.textContent = option.label;
      button.setAttribute('data-value', option.value);

      if (state.answers[question.id] === option.value) {
        button.classList.add('pgq__answer--selected');
      }

      button.addEventListener('click', function () {
        selectAnswer(question, option, button);
      });

      el.answers.appendChild(button);
    });

    el.backBtn.hidden = (state.currentIndex === 0);
    el.questionText.focus();
  }

  function selectAnswer(question, option, button) {
    state.answers[question.id] = option.value;

    Array.prototype.forEach.call(el.answers.children, function (child) {
      child.classList.remove('pgq__answer--selected');
      child.disabled = true;
    });
    button.classList.add('pgq__answer--selected');

    window.setTimeout(function () {
      if (state.currentIndex < QUESTIONS.length - 1) {
        state.currentIndex += 1;
        renderQuestion();
      } else {
        finishQuiz();
      }
    }, 300);
  }

  function goBack() {
    if (state.currentIndex > 0) {
      state.currentIndex -= 1;
      renderQuestion();
    }
  }

  /* ============================================================
     6. SCORING & RESULT LOGIC
     ============================================================ */
  function calculateScores() {
    var scores = {
      stoolPattern: 0,
      digestiveComfort: 0,
      foodLinked: 0,
      gutDisruption: 0
    };
    var redFlags = [];

    QUESTIONS.forEach(function (question) {
      var selected = getSelectedOption(question.id);
      if (!selected) return;

      if (selected.score) {
        Object.keys(selected.score).forEach(function (key) {
          scores[key] += selected.score[key];
        });
      }

      if (selected.redFlag) redFlags.push(selected.redFlag);
    });

    state.scores = scores;
    state.redFlags = dedupe(redFlags);
  }

  function getSignalStrength() {
    if (state.redFlags.length > 0) return 'Strong';

    var symptomMax = Math.max(
      state.scores.stoolPattern,
      state.scores.digestiveComfort,
      state.scores.foodLinked,
      state.scores.gutDisruption
    );

    if (symptomMax >= 6) return 'Strong';
    if (symptomMax >= 3) return 'Moderate';
    return 'Low';
  }

  function determineResult() {
    var scores = state.scores;
    var concern = state.answers.main_concern;
    var foodTrigger = state.answers.food_trigger;
    var recentSupport = state.answers.antibiotics_probiotics;

    state.signalStrength = getSignalStrength();

    // Warning signs always override educational result categories.
    if (state.redFlags.length > 0) {
      state.resultKey = 'vetFirst';
      return;
    }

    // A recent antibiotic course is specific and useful context.
    if (recentSupport === 'antibiotics_3mo') {
      state.resultKey = 'postAntibiotic';
      return;
    }

    // Respect the pet parent's stated main concern when supported.
    if (
      (concern === 'loose_inconsistent_stools' || concern === 'constipation') &&
      scores.stoolPattern >= 2
    ) {
      state.resultKey = 'stool';
      return;
    }

    if (concern === 'frequent_digestive_upset' && scores.digestiveComfort >= 2) {
      state.resultKey = 'sensitive';
      return;
    }

    // Explicitly reported food triggers are more meaningful than a
    // generic diet score. They are still described as possible patterns.
    if (
      ['chicken', 'beef', 'grains', 'rich_fatty', 'multiple_foods'].indexOf(foodTrigger) !== -1 &&
      scores.foodLinked >= 3
    ) {
      state.resultKey = 'foodLinked';
      return;
    }

    // Otherwise use the strongest remaining gut signal.
    if (scores.stoolPattern >= 4 && scores.stoolPattern >= scores.digestiveComfort) {
      state.resultKey = 'stool';
      return;
    }

    if (scores.digestiveComfort >= 3) {
      state.resultKey = 'sensitive';
      return;
    }

    if (scores.foodLinked >= 3) {
      state.resultKey = 'foodLinked';
      return;
    }

    state.resultKey = 'balanced';
  }

  function buildStandout() {
    var foodLabel = getSelectedLabel('food_trigger');
    var stoolLabel = getSelectedLabel('stool_quality');
    var warningLabel = getSelectedLabel('warning_signs');

    if (state.resultKey === 'vetFirst') {
      if (warningLabel && state.answers.warning_signs !== 'none') {
        return 'You reported “' + warningLabel + ',” which should be reviewed by a veterinarian before relying on an online gut-health result.';
      }
      return 'One of your answers included a warning sign that should be reviewed by a veterinarian first.';
    }

    if (state.resultKey === 'postAntibiotic') {
      return 'Recent antibiotic use stood out as an important part of your pet’s current gut-health history.';
    }

    if (state.resultKey === 'foodLinked') {
      if (state.answers.food_trigger === 'none_noticed' || state.answers.food_trigger === 'not_sure') {
        return 'Diet pattern and food changes stood out most in your answers.';
      }
      return 'You reported that ' + foodLabel.toLowerCase() + ' may be associated with digestive upset.';
    }

    if (state.resultKey === 'stool') {
      return 'Your pet’s usual stool was described as “' + stoolLabel + ',” making stool consistency the clearest pattern.';
    }

    if (state.resultKey === 'sensitive') {
      return 'Gas, bloating, stomach noises, or vomiting stood out more than the other gut-health signals.';
    }

    return 'Your answers were mostly steady across stool, digestive comfort, food-related patterns, and recent gut-health history.';
  }

  function buildPersonalizedInsights(result) {
    var insights = result.insights.slice(0, 3);
    var ageInsight = AGE_INSIGHTS[state.answers.age_group];

    if (ageInsight && state.resultKey !== 'vetFirst') {
      insights[2] = ageInsight;
    }

    return insights;
  }

  function finishQuiz() {
    calculateScores();
    determineResult();
    showScreen('email');
    el.firstName.focus();
  }

  /* ============================================================
     7. SHOPIFY EMAIL CAPTURE & RESULT UNLOCK

     This version uses Shopify's native customer form submission.
     JavaScript does not fetch the form. Before the browser submits,
     the completed quiz is saved in sessionStorage. Shopify then
     reloads the page, and the quiz restores the saved result.
     ============================================================ */
  function validateForm() {
    var emailValue = el.email.value.trim();
    var emailIsValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue);

    if (!el.firstName.value.trim()) return 'Please enter your first name.';
    if (!emailIsValid) return 'Please enter a valid email address.';
    if (!el.petName.value.trim()) return 'Please enter your pet’s name.';
    if (!el.consent.checked) {
      return 'Please check the consent box to save your email and view the full result.';
    }
    return null;
  }

  function handleSubmit(event) {
    var error = validateForm();

    if (error) {
      if (event) event.preventDefault();
      el.formError.textContent = error;
      el.formError.hidden = false;
      return;
    }

    el.formError.hidden = true;
    updateShopifyTags();

    if (!writeStoredJSON(STORAGE_PENDING_KEY, buildStoredSubmission())) {
      if (event) event.preventDefault();
      el.formError.textContent = 'Your browser blocked temporary quiz storage. Please allow site storage and try again.';
      el.formError.hidden = false;
      return;
    }

    // Do not prevent the default submit. Shopify's native customer
    // form now saves the email and reloads the page.
    el.submitBtn.disabled = true;
    el.submitBtn.textContent = 'Saving your email…';
  }

  function getServerFormStatus() {
    if (!el.formStatus) {
      return { postedSuccessfully: false, hasErrors: false, message: '' };
    }

    return {
      postedSuccessfully: el.formStatus.getAttribute('data-posted-successfully') === 'true',
      hasErrors: el.formStatus.getAttribute('data-has-errors') === 'true',
      message: (el.formStatus.textContent || '').trim()
    };
  }

  function restoreAfterShopifySubmit() {
    var pending = readStoredJSON(STORAGE_PENDING_KEY);
    var completed = readStoredJSON(STORAGE_COMPLETE_KEY);
    var serverStatus = getServerFormStatus();
    var search = new URLSearchParams(window.location.search);
    var shopifySuccessQuery = search.get('customer_posted') === 'true';

    if (pending) {
      restoreStoredSubmission(pending);

      if (serverStatus.hasErrors) {
        showScreen('email');
        el.formError.textContent = serverStatus.message || 'Shopify could not save that email. Please check it and try again.';
        el.formError.hidden = false;
        el.submitBtn.disabled = false;
        el.submitBtn.textContent = 'Save my email & see full result';
        return true;
      }

      // Shopify can return either form.posted_successfully? or the
      // customer_posted query parameter. Existing subscribers can
      // sometimes return neither, so a completed native form roundtrip
      // with no server error is also treated as a successful unlock.
      state.emailSaved = serverStatus.postedSuccessfully || shopifySuccessQuery || !serverStatus.hasErrors;
      writeStoredJSON(STORAGE_COMPLETE_KEY, pending);
      removeStoredValue(STORAGE_PENDING_KEY);
      renderResult();
      showScreen('result');
      return true;
    }

    if (completed && restoreStoredSubmission(completed)) {
      state.emailSaved = true;
      renderResult();
      showScreen('result');
      return true;
    }

    return false;
  }

  /* ============================================================
     8. RESULT-SCREEN SECTION BUILDERS
     ============================================================ */
  function buildBreakdownSection() {
    var section = document.createElement('div');
    section.className = 'pgq__breakdown';
    section.id = 'pgq-breakdown';

    var heading = document.createElement('h3');
    heading.className = 'pgq__subhead';
    heading.textContent = 'Your gut-signal breakdown';
    section.appendChild(heading);

    var note = document.createElement('p');
    note.className = 'pgq__micro-note';
    note.textContent = state.resultKey === 'vetFirst'
      ? 'A warning sign was reported. The levels below summarize quiz responses only and do not assess medical urgency.'
      : 'Signal strength reflects how strongly these patterns appeared in your answers. It is not a diagnosis or disease-risk score.';
    section.appendChild(note);

    var report = document.createElement('div');
    report.className = 'pgq__report';

    BREAKDOWN_ROWS.forEach(function (row) {
      var score = state.scores[row.key];
      var level = getLevel(score);
      var step = getLevelStep(level);

      var rowElement = document.createElement('div');
      rowElement.className = 'pgq__report-row';

      var label = document.createElement('span');
      label.className = 'pgq__report-label';
      label.textContent = row.label;

      var meter = document.createElement('span');
      meter.className = 'pgq__meter';
      meter.setAttribute('aria-hidden', 'true');

      for (var index = 1; index <= 4; index += 1) {
        var segment = document.createElement('span');
        segment.className = 'pgq__meter-seg' + (index <= step ? ' is-on' : '');
        meter.appendChild(segment);
      }

      var pill = document.createElement('span');
      pill.className = 'pgq__level pgq__level--' + level.toLowerCase();
      pill.textContent = level;

      rowElement.appendChild(label);
      rowElement.appendChild(meter);
      rowElement.appendChild(pill);
      report.appendChild(rowElement);
    });

    section.appendChild(report);
    return section;
  }

  function buildPrioritySection() {
    var section = document.createElement('div');
    section.className = 'pgq__priority';
    section.id = 'pgq-priority';

    var heading = document.createElement('h3');
    heading.className = 'pgq__subhead';
    heading.textContent = 'Your priority this week';
    section.appendChild(heading);

    var card = document.createElement('div');
    card.className = 'pgq__priority-card';

    if (state.resultKey === 'vetFirst') {
      card.classList.add('pgq__priority-card--vet-first');
    }

    var text = document.createElement('p');
    text.className = 'pgq__priority-text';
    text.textContent = PRIORITY_BY_RESULT[state.resultKey] || PRIORITY_BY_RESULT.balanced;

    card.appendChild(text);
    section.appendChild(card);
    return section;
  }

  function injectBreakdownAndPriority() {
    if (!el.fullResultDynamic) return;

    el.fullResultDynamic.innerHTML = '';
    el.fullResultDynamic.appendChild(buildBreakdownSection());
    el.fullResultDynamic.appendChild(buildPrioritySection());
  }

  function configureRecommendationLink(element, config, position) {
    if (!element) return;

    if (!config) {
      element.hidden = true;
      element.removeAttribute('href');
      return;
    }

    element.hidden = false;
    element.textContent = config.label;
    element.href = buildTrackedProductUrl(config.product, config.trackingContent);
    element.setAttribute('data-pgq-recommendation-link', 'true');
    element.setAttribute('data-product', config.product);
    element.setAttribute('data-position', position);

    if (position === 'primary') {
      element.className = 'pgq__btn pgq__recommendation-btn ' + (
        config.style === 'secondary' ? 'pgq__btn--secondary' : 'pgq__btn--primary'
      );
    } else if (position === 'secondary') {
      element.className = 'pgq__btn pgq__btn--secondary pgq__recommendation-btn';
    } else {
      element.className = 'pgq__recommendation-link';
    }
  }

  function renderRecommendation() {
    var recommendation = RECOMMENDATIONS[state.resultKey] || RECOMMENDATIONS.balanced;
    if (!el.recommendation) return;

    el.recommendation.className = 'pgq__recommendation';
    if (recommendation.tone === 'vetFirst') {
      el.recommendation.classList.add('pgq__recommendation--vet-first');
    }

    el.recommendationTitle.textContent = recommendation.title;
    el.recommendationBody.textContent = recommendation.body;

    configureRecommendationLink(el.recommendationPrimary, recommendation.primary, 'primary');
    configureRecommendationLink(el.recommendationSecondary, recommendation.secondary, 'secondary');
    configureRecommendationLink(el.recommendationTertiary, recommendation.tertiary, 'tertiary');

    if (recommendation.note) {
      el.recommendationNote.textContent = recommendation.note;
      el.recommendationNote.hidden = false;
    } else {
      el.recommendationNote.textContent = '';
      el.recommendationNote.hidden = true;
    }
  }

  /* ============================================================
     9. RESULT RENDERING
     ============================================================ */
  function renderResult() {
    var result = RESULTS[state.resultKey];
    var insights = buildPersonalizedInsights(result);

    el.resultType.textContent = result.type;
    el.resultHeadline.textContent = result.headline;
    el.signalStrength.textContent = state.signalStrength;
    el.signalStrength.className = 'pgq__signal-badge pgq__signal-badge--' + state.signalStrength.toLowerCase();
    el.resultExplanation.textContent = result.explanation;
    el.resultStandout.textContent = buildStandout();

    injectBreakdownAndPriority();

    el.resultInsights.innerHTML = '';
    insights.forEach(function (text) {
      var listItem = document.createElement('li');
      listItem.textContent = text;
      el.resultInsights.appendChild(listItem);
    });

    el.resultTracking.innerHTML = '';
    result.tracking.forEach(function (text) {
      var listItem = document.createElement('li');
      listItem.textContent = text;
      el.resultTracking.appendChild(listItem);
    });

    el.redflag.hidden = (state.redFlags.length === 0);

    if (el.fullResult) el.fullResult.hidden = false;
    if (el.captureSuccess) {
      el.captureSuccess.textContent = 'Thanks, ' + el.firstName.value.trim() + '. ' + el.petName.value.trim() + '’s full result is below.';
      el.captureSuccess.hidden = false;
    }

    renderRecommendation();
  }

  /* ============================================================
     10. EVENT BINDINGS
     ============================================================ */
  el.startBtn.addEventListener('click', function () {
    removeStoredValue(STORAGE_PENDING_KEY);
    removeStoredValue(STORAGE_COMPLETE_KEY);
    showScreen('quiz');
    renderQuestion();
  });

  el.backBtn.addEventListener('click', goBack);

  if (el.captureForm) {
    el.captureForm.addEventListener('submit', handleSubmit);
  }

  if (el.recommendation) {
    el.recommendation.addEventListener('click', trackRecommendationClick);
  }

  restoreAfterShopifySubmit();
})();
